import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, message } from 'antd';
import { d204Api, FD140 } from '../../services/d204Api';
import { useD204Mode } from './hooks/useD204Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { useD204Record } from './hooks/useD204Record';
import { useD204Navigation } from './hooks/useD204Navigation';
import { useD204Permissions } from './hooks/useD204Permissions';
import { useD204Approval } from './hooks/useD204Approval';
import PermissionGuard from '../../components/PermissionGuard';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Product from './components/Tab2Product';
import Tab3Detail from './components/Tab3Detail';
import Tab4LotDetail from './components/Tab4LotDetail';
import Tab5Attachment from './components/Tab5Attachment';
import { SharedActionBar } from '../../components/SharedActionBar';
import { BatchAcceptanceBridge, pwd204f1AcceptanceManifest } from '../../features/acceptance';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import { getStatusBadge, isFieldDisabledFn, isLockedForEdit } from './helpers';
import './D204Form.css';

const D204Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const perms = useD204Permissions();
  const { mode, setMode, isEditing } = useD204Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details, lotDetails,
    selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    addLotRow, deleteLotRow, setLotField,
    revertToRecord, resetForAdd, applyResult, importSubcontractOrder,
    getFieldError,
  } = useD204Record();

  useEffect(()=>{if(mode==='add'&&formData.fd140_11){void importSubcontractOrder(String(formData.fd140_11));}},[mode,formData.fd140_11,importSubcontractOrder]);

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useD204Navigation(loadRecordWithNames);

  // Approval hooks
  const {
    approvalHistoryVisible, setApprovalHistoryVisible,
    handleSubmit, handleApprove, handleReject, handleWriteoff, handleViewHistory,
  } = useD204Approval({ record, isEditing, loadRecordWithNames });

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (reviewId) {
          const idx = ids.indexOf(reviewId);
          if (idx >= 0) setCurrentIdx(idx);
          await loadRecordWithNames(reviewId);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load D204 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames, setCurrentIdx]);

  // Mode actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: FD140) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fd140_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除入庫單 ${record.fd140_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  // Computed
  const statusBadge = getStatusBadge(record || formData);
  const isFieldDisabled = useCallback(
    (key: string) => isFieldDisabledFn(key, isEditing, mode),
    [isEditing, mode]
  );

  return (
    <PermissionGuard module={'pwd204f1'} action="query">
    <div className="d204-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <span className="current">託工入庫單</span>
            </nav>
            <span className="title-separator">—</span>
            <h1>託工入庫單 Subcontract Receipt</h1>
            <span className="badge badge-default">D204</span>
            {statusBadge && (
              <span
                className={`badge field-label ${statusBadge.cls}`}
                data-contract-field="fd140_80"
              >
                核准狀態：{statusBadge.text}
              </span>
            )}
          </div>
          <div className="page-title-right" />
        </div>

        {/* Form Info Bar */}
        <div className="form-info-bar">
          <div className="field-group">
            <label>入庫單號</label>
            <input type="text" readOnly style={{ width: 140 }} value={formData.fd140_01 || ''} data-contract-field="fd140_01" />
          </div>
          <div className="field-group">
            <label>加工廠</label>
            <span style={{ fontSize: 13 }} data-contract-field="fd140_10">{names.processor || '--'}</span>
          </div>
          <div className="field-group">
            <label>託工單號</label>
            <span style={{ fontSize: 13 }} data-contract-field="fd140_11">{formData.fd140_11 || '--'}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {['基本資料', '產品說明', '入庫明細', '板/缸號', '附屬明細'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo
              formData={formData} names={names} isEditing={isEditing}
              isFieldDisabled={isFieldDisabled} getField={getField} setField={setField}
              resolveSingleName={resolveSingleName} getFieldError={getFieldError}
            />
          </div>

          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2Product
              formData={formData} isEditing={isEditing}
              isFieldDisabled={isFieldDisabled} getField={getField} setField={setField}
            />
          </div>

          <div className={`tab-pane ${activeTab === 2 ? 'active' : ''}`}>
            <Tab3Detail
              formData={formData} details={details} selectedRow={selectedRow}
              isEditing={isEditing} setSelectedRow={setSelectedRow}
              getDetailField={getDetailField} setDetailField={setDetailField}
              addDetailRow={addDetailRow} deleteDetailRow={deleteDetailRow}
            />
          </div>

          <div className={`tab-pane ${activeTab === 3 ? 'active' : ''}`}>
            <Tab4LotDetail
              details={details} lotDetails={lotDetails}
              selectedRow={selectedRow}
              isEditing={isEditing} setSelectedRow={setSelectedRow}
              onAddLot={addLotRow}
              onDeleteLot={deleteLotRow}
              onLotFieldChange={setLotField}
            />
          </div>

          <div className={`tab-pane ${activeTab === 4 ? 'active' : ''}`}>
            <Tab5Attachment
              formData={formData}
              attachments={formData.attachments || []}
              isEditing={isEditing}
            />
          </div>
        </div>

        {/* Bottom Action Bar */}
        <SharedActionBar
          mode={mode} isEditing={isEditing} hasRecord={!!record}
          currentIdx={currentIdx} recordCount={allIds.length}
          onNavFirst={() => handleNavTo(0)} onNavPrev={() => handleNavTo(currentIdx - 1)}
          onNavNext={() => handleNavTo(currentIdx + 1)} onNavLast={() => handleNavTo(allIds.length - 1)}
          onAdd={handleAddAction} onModify={handleModifyAction} onDelete={handleDeleteAction}
          onSave={handleAddAction} onCancel={handleModifyAction}
          approval={{
            statusCode: (record?.fd140_80 || '').trim(),
            isApproved: (record?.fd140_80 || '').trim() === 'P',
            isPending: ['D', 'S'].includes((record?.fd140_80 || '').trim()), // 統一碼 D + 舊碼 S 過渡容忍
            isLocked: isLockedForEdit(record),
          }}
          onSubmit={handleSubmit} onApprove={handleApprove}
          onReject={handleReject} onWriteoff={handleWriteoff} onHistory={handleViewHistory}
          onExit={() => navigate('/erp')}
          canCreate={perms.canCreate} canEdit={perms.canEdit}
          canDelete={perms.canDelete} canApprove={perms.canApprove}
          acceptanceActionIds={{
            list: 'PWD204F1-ACT-LIST', get: 'PWD204F1-ACT-GET',
            create: 'PWD204F1-ACT-CREATE', update: 'PWD204F1-ACT-UPDATE',
            delete: 'PWD204F1-ACT-DELETE', submit: 'PWD204F1-ACT-SUBMIT',
            approve: 'PWD204F1-ACT-APPROVE', reject: 'PWD204F1-ACT-REJECT',
            writeoff: 'PWD204F1-ACT-WRITEOFF',
          }}
        />
        <BatchAcceptanceBridge
          manifest={pwd204f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fd140_01 ? [record.fd140_01] : []}
          keyOf={(item) => item.fd140_01}
          queryExact={(key) => d204Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && handleNavTo(0)}
          autoFillDisabled={allIds.length === 0}
          autoFillReason="載入正式 API 已存在的第一筆託工入庫單作範例，不直接寫業務資料。"
          screenDraft={{ master: formData, details }}
        />
      </div>

      {/* Approval History Modal */}
      {record && (
        <ApprovalHistoryModal
          visible={approvalHistoryVisible}
          orderNo={record.fd140_01}
          onClose={() => setApprovalHistoryVisible(false)}
        />
      )}
    </div>
    </PermissionGuard>
  );
};

export default D204Form;
